import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ArrowRight } from 'lucide-react';
import { previewInvite, registerIndividual, startOAuthLogin } from '../services/auth';
import { usePilotFlags } from '../config/pilotFlags';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTabSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

function SignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialInviteToken = searchParams.get('token') || '';
  const [activeTab, setActiveTab] = useState(initialInviteToken ? 'invite' : 'individual');
  const { hideOAuthButtons: oauthButtonsHidden } = usePilotFlags();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'student',
    password: '',
    confirmPassword: '',
    marketingOptIn: false,
    agreeTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState({ google: false, microsoft: false });

  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  const [inviteError, setInviteError] = useState('');
  const [previewingInvite, setPreviewingInvite] = useState(false);

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email || '');
  const validatePassword = (password) => password && password.length >= 8;

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.firstName.trim()) nextErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) nextErrors.lastName = 'Last name is required';
    if (!validateEmail(form.email)) nextErrors.email = 'Enter a valid email address';
    if (!form.role) nextErrors.role = 'Please select a role';
    if (!validatePassword(form.password)) nextErrors.password = 'Password must be at least 8 characters';
    if (form.confirmPassword !== form.password) nextErrors.confirmPassword = 'Passwords do not match';
    if (!form.agreeTerms) nextErrors.agreeTerms = 'You must accept the terms to continue';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await registerIndividual(form);
      setSuccessMsg(response.message || 'Registration successful. Check your inbox for verification instructions.');
    } catch (error) {
      setErrorMsg(error.response?.data?.message || 'An error occurred during registration.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuth = async (provider) => {
    setOauthLoading((prev) => ({ ...prev, [provider]: true }));
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const result = await startOAuthLogin(provider);
      if (result.status === 'authenticated') {
        navigate('/dashboard', { replace: true });
      } else if (result.status === 'error') {
        setErrorMsg(result.message || `Couldn't sign up with ${provider}.`);
      }
    } catch (error) {
      setErrorMsg(`Couldn't sign up with ${provider}.`);
    } finally {
      setOauthLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setInviteError('');

    if (!inviteToken.trim()) {
      setInviteError('Please enter a valid invite token.');
      return;
    }

    setPreviewingInvite(true);
    try {
      await previewInvite(inviteToken);
      navigate(`/register/invite?token=${encodeURIComponent(inviteToken.trim())}`);
    } catch (error) {
      setInviteError('Invalid or expired invite link.');
    } finally {
      setPreviewingInvite(false);
    }
  };

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Sign up"
        title="Sign up"
        description="Choose how you would like to join Virtual Medic."
      />

      <Tabs
        value={activeTab}
        onChange={(_event, value) => setActiveTab(value)}
        variant="fullWidth"
        sx={{
          mb: 3,
          p: 0.5,
          borderRadius: '18px',
          bgcolor: publicPalette.heroBlueCloud,
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        <Tab
          label="Sign up individually"
          value="individual"
          sx={{
            ...publicTabSx,
            borderRadius: '14px',
            bgcolor: activeTab === 'individual' ? '#ffffff' : 'transparent',
          }}
        />
        <Tab
          label="Join with invite"
          value="invite"
          sx={{
            ...publicTabSx,
            borderRadius: '14px',
            bgcolor: activeTab === 'invite' ? '#ffffff' : 'transparent',
          }}
        />
      </Tabs>

      {activeTab === 'individual' ? (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {errorMsg && <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>}
          {successMsg && <Alert severity="success" sx={{ mb: 3 }}>{successMsg}</Alert>}

          {!oauthButtonsHidden && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleOAuth('google')}
                    disabled={oauthLoading.google}
                    startIcon={
                      oauthLoading.google ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Box
                          component="img"
                          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                          alt="Google"
                          sx={{ width: 20, height: 20 }}
                        />
                      )
                    }
                    sx={{ ...publicSecondaryButtonSx, minHeight: 50, width: '100%' }}
                  >
                    {oauthLoading.google ? 'Connecting...' : 'Sign up with Google'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleOAuth('microsoft')}
                    disabled={oauthLoading.microsoft}
                    startIcon={
                      oauthLoading.microsoft ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Box
                          component="img"
                          src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                          alt="Microsoft"
                          sx={{ width: 18, height: 18 }}
                        />
                      )
                    }
                    sx={{ ...publicSecondaryButtonSx, minHeight: 50, width: '100%' }}
                  >
                    {oauthLoading.microsoft ? 'Connecting...' : 'Sign up with Microsoft'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 3 }}>
            <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
              {oauthButtonsHidden ? 'Sign up with email' : 'Or sign up with email'}
            </Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                sx={publicTextFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                sx={publicTextFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Email address"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email || 'We will send verification instructions here.'}
                sx={publicTextFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                required
                fullWidth
                label="Role"
                name="role"
                value={form.role}
                onChange={handleChange}
                error={Boolean(errors.role)}
                helperText={errors.role || 'Select your primary role.'}
                sx={publicTextFieldSx}
              >
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="doctor">Doctor</MenuItem>
                <MenuItem value="educator">Educator</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                sx={publicTextFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Confirm password"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={handleChange}
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword}
                sx={publicTextFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="marketingOptIn" checked={form.marketingOptIn} onChange={handleChange} />}
                label="Keep me informed about new cases and product updates"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="agreeTerms" checked={form.agreeTerms} onChange={handleChange} />}
                label={(
                  <span>
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                  </span>
                )}
              />
              {errors.agreeTerms && (
                <Typography variant="caption" color="error">
                  {errors.agreeTerms}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Button type="submit" fullWidth variant="contained" disabled={submitting} sx={{ ...publicPrimaryButtonSx, mt: 3 }}>
            {submitting ? 'Creating account...' : 'Sign up as an individual'}
          </Button>
        </Box>
      ) : (
        <Stack spacing={3}>
          <Alert severity="info">
            Use the invite token from your email to continue with a university-provisioned registration flow.
          </Alert>

          <Box component="form" onSubmit={handleInviteSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label="Invite token"
                value={inviteToken}
                onChange={(event) => setInviteToken(event.target.value)}
                placeholder="Paste the invite token from your email"
                sx={publicTextFieldSx}
              />
              {inviteError && <Alert severity="error">{inviteError}</Alert>}
              <Button
                type="submit"
                variant="contained"
                disabled={previewingInvite}
                sx={{ ...publicPrimaryButtonSx, alignSelf: 'flex-start' }}
              >
                {previewingInvite ? 'Checking invite...' : 'Continue with invite'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}

      <Divider sx={{ my: 4 }}>
        <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
          Already have an account?
        </Typography>
      </Divider>

      <Button component={Link} to="/login" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
        Log in instead
      </Button>
    </PublicPageFrame>
  );
}

export default SignupPage;
